import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useTimeZone } from '@connect-x-app/hybrid-state';
import { Dot, Text } from '@connect-x-app/hybrid-ui';
import { FlightRoomSummary } from '@connect-x-app/shared-domain-models';
import { IconName } from '@connect-x-app/shared-types';
import { COLORS } from '@connect-x-app/shared-ui';
import { isAfterDateTime } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Icon, Tooltip } from '@connect-x-app/web-ui';
import { ReferenceType } from '@floating-ui/react';
import { clsx } from 'clsx';

import { Mute } from '../mute';

import './FlightroomItem.scss';

type Props = {
  disabled?: boolean;
  displayAccessState?: boolean;
  displayJoinIcon?: boolean;
  displayTaskIcon?: boolean;
  displayUnreadCount?: boolean;
  flightroom: FlightRoomSummary;
  hasUnreadMentions?: boolean;
  isSelected?: boolean;
  onClick: () => void;
  onClickTask?: () => void;
  showEstimatedTimes?: boolean;
  showMute?: boolean;
  unreadCount?: number | null;
};

const getIconName = (
  isActiveMemberOfRoom: boolean,
  canRequestAccess: boolean,
  canJoinFlightRooms: boolean,
  hasPendingAccessRequest?: boolean,
  displayAccessState?: boolean,
): IconName | null => {
  if ((displayAccessState && hasPendingAccessRequest) || isActiveMemberOfRoom) {
    return 'circularCheckmarkDisabled';
  }
  if (canRequestAccess || canJoinFlightRooms) {
    return 'circularPlusFilledRegular';
  }
  return null;
};

export const FlightroomItem: FC<Props> = ({
  disabled = false,
  displayAccessState,
  displayJoinIcon,
  displayTaskIcon,
  displayUnreadCount,
  flightroom,
  hasUnreadMentions,
  isSelected,
  onClick,
  onClickTask,
  showEstimatedTimes,
  showMute,
  unreadCount,
}) => {
  const [tooltipAnchor, setTooltipAnchor] = useState<ReferenceType | null>(null);
  const { canJoinFlightRooms, canRequestAccess, hasGrantAccess, hasPendingAccessRequest, isActiveMemberOfRoom } =
    useAuthentication();

  const { t } = useTranslation();
  const { formatShortDateInIanaTimeZone, formatTimeInIanaTimeZone } = useTimeZone();

  const isDisabled = useMemo(() => {
    const hasRequestedAccessOrIsTask = hasPendingAccessRequest(flightroom) || displayTaskIcon;
    return disabled || (hasRequestedAccessOrIsTask && !canJoinFlightRooms);
  }, [canJoinFlightRooms, disabled, displayTaskIcon, flightroom, hasPendingAccessRequest]);

  const flightroomItemClasses = useMemo(
    () =>
      clsx({
        disabled: isDisabled,
        'flightroom-item--mute': showMute,
        selected: isSelected,
      }),
    [isDisabled, isSelected, showMute],
  );

  const renderEstimatedOrActualTime = (
    scheduled: string,
    estimated?: string | null,
    actual?: string | null,
    ianaTimeZone?: string | null,
    alignRight?: boolean,
  ) => {
    if (!showEstimatedTimes || (!estimated && !actual)) {
      return null;
    }

    const time = actual ?? estimated;
    const isDelayed = time && isAfterDateTime(time, scheduled);

    return (
      <Text
        className={clsx({
          actual: !!actual,
          delay: isDelayed,
          'left-align': !alignRight,
          'right-align': alignRight,
        })}
        size="SM"
        weight="bold"
      >
        {time ? (formatTimeInIanaTimeZone(time, ianaTimeZone ?? '') ?? '') : ''}
      </Text>
    );
  };

  const handleClick = useCallback(() => {
    if (!isDisabled) {
      onClick();
    }
  }, [isDisabled, onClick]);

  const notifications = useMemo(() => {
    let count = unreadCount ?? 0;
    if (hasGrantAccess && isActiveMemberOfRoom(flightroom)) {
      count += flightroom.accessRequestCount ?? 0;
    }
    return count;
  }, [flightroom, hasGrantAccess, isActiveMemberOfRoom, unreadCount]);

  const iconName = useMemo(() => {
    return getIconName(
      isActiveMemberOfRoom(flightroom),
      canRequestAccess,
      canJoinFlightRooms,
      displayAccessState,
      hasPendingAccessRequest(flightroom),
    );
  }, [canJoinFlightRooms, canRequestAccess, displayAccessState, flightroom, hasPendingAccessRequest, isActiveMemberOfRoom]);

  return (
    <div className={clsx('flightroom-item', flightroomItemClasses)}>
      <article
        className="flightroom-container"
        data-test={FLIGHTROOM_TEST_IDS.item.row(flightroom.flightNumber)}
        onClick={handleClick}
        role="button"
      >
        <Tooltip alignment="top" anchor={tooltipAnchor}>
          <slot name="content">
            <span>{t('FLIGHTROOMS.ASSIGN_TASK.TOOLTIP')}</span>
          </slot>
        </Tooltip>
        <header className="header-container">
          <div className="header-content">
            <Text size="MD" weight="bold">
              {flightroom.flightNumber}
            </Text>
            <Text size="SM">{flightroom.aircraft}</Text>
          </div>
          <div className="header-right-content">
            {isActiveMemberOfRoom(flightroom) && hasUnreadMentions && !isSelected && (
              <Icon name="mention" size={2} testId={FLIGHTROOM_TEST_IDS.item.mentionIcon} />
            )}
            {displayAccessState && hasPendingAccessRequest(flightroom) && (
              <Icon
                colorName={isSelected ? COLORS.yuma.$01 : COLORS.zulu.$08}
                name="securityLockOutline"
                size={2.4}
                testId={FLIGHTROOM_TEST_IDS.item.pendingRequestIcon}
              />
            )}
            {isActiveMemberOfRoom(flightroom) && displayUnreadCount && !isSelected && notifications > 0 && (
              <Dot size="l" testId={FLIGHTROOM_TEST_IDS.item.notificationCounter} value={notifications} />
            )}
            {displayJoinIcon && iconName && (
              <Icon
                name={iconName}
                size={2.5}
                testId={
                  iconName === 'circularCheckmarkDisabled'
                    ? FLIGHTROOM_TEST_IDS.item.joinCheckIcon
                    : FLIGHTROOM_TEST_IDS.item.joinPlusIcon
                }
              />
            )}
            {!hasPendingAccessRequest(flightroom) && displayTaskIcon && (
              <button
                className="assign-task-button"
                data-test={FLIGHTROOM_TEST_IDS.item.assignTask}
                disabled={hasPendingAccessRequest(flightroom)}
                onClick={event => {
                  event.stopPropagation();
                  onClickTask?.();
                }}
              >
                <Icon
                  colorName={isSelected ? COLORS.yuma.$01 : COLORS.zulu.$08}
                  disabled={hasPendingAccessRequest(flightroom)}
                  name={'assignTask'}
                  onHover={anchor => {
                    setTooltipAnchor(anchor.currentTarget);

                    return () => setTooltipAnchor(null);
                  }}
                  size={2.4}
                />
              </button>
            )}
          </div>
        </header>
        <section className="body">
          <div className="departure-airport">
            {flightroom.departure.airport.icao ? (
              <Text className="airport-name" testID={FLIGHTROOM_TEST_IDS.item.airportIcao('left')}>
                {flightroom.departure.airport.icao}
              </Text>
            ) : null}
            {flightroom.departure.airport.iata ? (
              <Text className="airport-name" testID={FLIGHTROOM_TEST_IDS.item.airportIata('left')}>
                {flightroom.departure.airport.iata}
              </Text>
            ) : null}
          </div>
          <div className="center">
            <div className="time-row">
              <Text size="SM" weight="semiBold">
                {formatTimeInIanaTimeZone(flightroom.schedule.std, flightroom.departure.timeZone ?? '') ?? ''}
              </Text>
              <div className="icon-wrapper">
                <div className="icon-seperator left"></div>
                <Icon name="aircraftPlaneRightFilled" size={1.6} />
                <div className="icon-seperator right"></div>
              </div>
              <Text size="SM" weight="semiBold">
                {formatTimeInIanaTimeZone(flightroom.schedule.sta, flightroom.destination.timeZone ?? '') ?? ''}
              </Text>
            </div>
            <div className="time-row">
              <div className="delay-value">
                {renderEstimatedOrActualTime(
                  flightroom.schedule.std,
                  flightroom.schedule.etd,
                  flightroom.schedule.atd,
                  flightroom.departure.timeZone,
                )}
              </div>
              <Text className="date" size="SM" weight="semiBold">
                {formatShortDateInIanaTimeZone(flightroom.schedule.std, flightroom.departure.timeZone ?? '') ?? ''}
              </Text>
              <div className="delay-value">
                {renderEstimatedOrActualTime(
                  flightroom.schedule.sta,
                  flightroom.schedule.eta,
                  flightroom.schedule.ata,
                  flightroom.destination.timeZone,
                  true,
                )}
              </div>
            </div>
          </div>
          <div className="arrival-airport">
            {flightroom.destination.airport.icao ? (
              <Text className="airport-name" testID={FLIGHTROOM_TEST_IDS.item.airportIcao('right')}>
                {flightroom.destination.airport.icao}
              </Text>
            ) : null}
            {flightroom.destination.airport.iata ? (
              <Text className="airport-name" testID={FLIGHTROOM_TEST_IDS.item.airportIata('right')}>
                {flightroom.destination.airport.iata}
              </Text>
            ) : null}
          </div>
        </section>
      </article>
      {showMute ? (
        <Mute
          className="flightroom__footer"
          flightroomId={flightroom.externalId}
          isDisabled={isDisabled}
          isSelected={isSelected}
          muted={flightroom.muted}
          mutedUntilDurationBeforeDeparture={flightroom.mutedUntilDurationBeforeDeparture}
        />
      ) : null}
    </div>
  );
};
